import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
  Routes,
} from '@angular/router';
import { PositizeService } from './services/positize.service';

const canActivate: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  return inject(PositizeService).canActivate();
};

const canAdminActivate: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  return inject(PositizeService).canActivate(true);
};

export const routes: Routes = [
  // --- Platform Admin routes ---
  {
    path: 'admin',
    loadChildren: () =>
      import('./admin.routes').then((m) => m.ADMIN_ROUTES),
  },
  // --- Account routes ---
  {
    path: 'account',
    loadChildren: () =>
      import('./account.routes').then((m) => m.ACCOUNT_ROUTES),
  },
  // --- Shop routes ---
  {
    path: 'login',
    loadComponent: () =>
      import('./components/no-header/no-header.component').then(
        (m) => m.NoHeaderComponent
      ),
    children: [
      {
        path: '**',
        loadComponent: () =>
          import('./pages/login/login.component').then((m) => m.LoginComponent),
      },
    ],
  },
  // --- Public appointment booking page of a shop (no Positize session) ---
  {
    path: 'rdv',
    loadComponent: () =>
      import('./components/no-header/no-header.component').then(
        (m) => m.NoHeaderComponent
      ),
    children: [
      {
        path: ':shop',
        loadComponent: () =>
          import('./pages/booking/booking.component').then((m) => m.BookingComponent),
      },
    ],
  },
  {
    path: 'start',
    loadComponent: () =>
      import('./components/no-header/no-header.component').then(
        (m) => m.NoHeaderComponent
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/start/start.component').then((m) => m.StartComponent),
      },
      {
        path: ':shopident',
        loadComponent: () =>
          import('./pages/start/start.component').then((m) => m.StartComponent),
      },
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('./components/top-header/top-header.component').then(
        (m) => m.TopHeaderComponent
      ),
    children: [
      {
        path: '',
        redirectTo: '/start',
        pathMatch: 'full',
      },
      {
        path: 'customers',
        loadComponent: () =>
          import('./pages/customers/customers.component').then(
            (m) => m.CustomersComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'customer-groups',
        loadComponent: () =>
          import('./pages/customer-groups/customer-groups.component').then(
            (m) => m.CustomerGroupsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'products',
        loadComponent: () =>
          import('./pages/products/products.component').then(
            (m) => m.ProductsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'tax-rates',
        loadComponent: () =>
          import('./pages/tax-rates/tax-rates.component').then(
            (m) => m.TaxRatesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'attributes',
        loadComponent: () =>
          import('./pages/attributes/attributes.component').then(
            (m) => m.AttributesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'categories',
        loadComponent: () =>
          import('./pages/categories/categories.component').then(
            (m) => m.CategoriesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'brands',
        loadComponent: () =>
          import('./pages/brands/brands.component').then(
            (m) => m.BrandsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'product-tags',
        loadComponent: () =>
          import('./pages/product-tags/product-tags.component').then(
            (m) => m.ProductTagsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'customer-tags',
        loadComponent: () =>
          import('./pages/customer-tags/customer-tags.component').then(
            (m) => m.CustomerTagsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'suppliers',
        loadComponent: () =>
          import('./pages/suppliers/suppliers.component').then(
            (m) => m.SuppliersComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'shop-config',
        loadComponent: () =>
          import('./pages/shop-config/shop-config.component').then(
            (m) => m.ShopConfigComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'payment-methods',
        loadComponent: () =>
          import('./pages/payment-methods/payment-methods.component').then(
            (m) => m.PaymentMethodsComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'discounts',
        loadComponent: () =>
          import('./pages/discounts/discounts.component').then(
            (m) => m.DiscountsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'services',
        loadComponent: () =>
          import('./pages/services/services.component').then(
            (m) => m.ServicesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'service-configuration',
        loadComponent: () =>
          import(
            './pages/service-configuration/service-configuration.component'
          ).then((m) => m.ServiceConfigurationComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'service-dashboard',
        loadComponent: () =>
          import('./pages/service-dashboard/service-dashboard.component').then(
            (m) => m.ServiceDashboardComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'subscription-dashboard',
        loadComponent: () =>
          import(
            './pages/subscription-dashboard/subscription-dashboard.component'
          ).then((m) => m.SubscriptionDashboardComponent),
        canActivate: [canActivate],
      },
      {
        path: 'subscriptions',
        loadComponent: () =>
          import('./pages/subscriptions/subscriptions.component').then(
            (m) => m.SubscriptionsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'subscription-configuration',
        loadComponent: () =>
          import(
            './pages/subscription-configuration/subscription-configuration.component'
          ).then((m) => m.SubscriptionConfigurationComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'donation-dashboard',
        loadComponent: () =>
          import(
            './pages/donation-dashboard/donation-dashboard.component'
          ).then((m) => m.DonationDashboardComponent),
        canActivate: [canActivate],
      },
      {
        path: 'donations',
        loadComponent: () =>
          import('./pages/donations/donations.component').then(
            (m) => m.DonationsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'donation-attestations',
        loadComponent: () =>
          import(
            './pages/donation-attestations/donation-attestations.component'
          ).then((m) => m.DonationAttestationsComponent),
        canActivate: [canActivate],
      },
      {
        path: 'donation-tags',
        loadComponent: () =>
          import('./pages/donation-tags/donation-tags.component').then(
            (m) => m.DonationTagsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'donation-configuration',
        loadComponent: () =>
          import(
            './pages/donation-configuration/donation-configuration.component'
          ).then((m) => m.DonationConfigurationComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'donation-statistics',
        loadComponent: () =>
          import(
            './pages/donation-statistics/donation-statistics.component'
          ).then((m) => m.DonationStatisticsComponent),
        canActivate: [canActivate],
      },
      {
        path: 'crm-dashboard',
        loadComponent: () =>
          import('./pages/crm-dashboard/crm-dashboard.component').then(
            (m) => m.CrmDashboardComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'crm-leads',
        loadComponent: () =>
          import('./pages/crm-leads/crm-leads.component').then(
            (m) => m.CrmLeadsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'memberships',
        loadComponent: () =>
          import('./pages/memberships/memberships.component').then(
            (m) => m.MembershipsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'membership-seasons',
        loadComponent: () =>
          import(
            './pages/membership-seasons/membership-seasons.component'
          ).then((m) => m.MembershipSeasonsComponent),
        canActivate: [canActivate],
      },
      {
        path: 'membership-configuration',
        loadComponent: () =>
          import(
            './pages/membership-configuration/membership-configuration.component'
          ).then((m) => m.MembershipConfigurationComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'real-estate-dashboard',
        loadComponent: () =>
          import('./pages/real-estate-dashboard/real-estate-dashboard.component').then(
            (m) => m.RealEstateDashboardComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'properties',
        loadComponent: () =>
          import('./pages/properties/properties.component').then(
            (m) => m.PropertiesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'property/:id',
        loadComponent: () =>
          import('./pages/property/property.component').then(
            (m) => m.PropertyComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'leases',
        loadComponent: () =>
          import('./pages/leases/leases.component').then(
            (m) => m.LeasesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'property-expenses',
        loadComponent: () =>
          import('./pages/property-expenses/property-expenses.component').then(
            (m) => m.PropertyExpensesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'property-deadlines',
        loadComponent: () =>
          import('./pages/property-deadlines/property-deadlines.component').then(
            (m) => m.PropertyDeadlinesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'rents',
        loadComponent: () =>
          import('./pages/rents/rents.component').then(
            (m) => m.RentsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'rent-indexations',
        loadComponent: () =>
          import('./pages/rent-indexations/rent-indexations.component').then(
            (m) => m.RentIndexationsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'charges-statements',
        loadComponent: () =>
          import('./pages/charges-statements/charges-statements.component').then(
            (m) => m.ChargesStatementsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'property-interventions',
        loadComponent: () =>
          import('./pages/property-interventions/property-interventions.component').then(
            (m) => m.PropertyInterventionsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'management-mandates',
        loadComponent: () =>
          import('./pages/management-mandates/management-mandates.component').then(
            (m) => m.ManagementMandatesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'real-estate-tax-summary',
        loadComponent: () =>
          import('./pages/real-estate-tax-summary/real-estate-tax-summary.component').then(
            (m) => m.RealEstateTaxSummaryComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'real-estate-configuration',
        loadComponent: () =>
          import('./pages/real-estate-configuration/real-estate-configuration.component').then(
            (m) => m.RealEstateConfigurationComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'team-planning',
        loadComponent: () =>
          import('./pages/team-planning/team-planning.component').then(
            (m) => m.TeamPlanningComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'team-my-week',
        loadComponent: () =>
          import('./pages/team-my-week/team-my-week.component').then(
            (m) => m.TeamMyWeekComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'team-tasks',
        loadComponent: () =>
          import('./pages/team-tasks/team-tasks.component').then(
            (m) => m.TeamTasksComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'team-stats',
        loadComponent: () =>
          import('./pages/team-stats/team-stats.component').then(
            (m) => m.TeamStatsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'finance-dashboard',
        loadComponent: () =>
          import('./pages/finance-dashboard/finance-dashboard.component').then(
            (m) => m.FinanceDashboardComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'expenses',
        loadComponent: () =>
          import('./pages/expenses/expenses.component').then(
            (m) => m.ExpensesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'finance-vat',
        loadComponent: () =>
          import('./pages/finance-vat/finance-vat.component').then(
            (m) => m.FinanceVatComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'recurring-expenses',
        loadComponent: () =>
          import('./pages/recurring-expenses/recurring-expenses.component').then(
            (m) => m.RecurringExpensesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'finance-configuration',
        loadComponent: () =>
          import('./pages/finance-configuration/finance-configuration.component').then(
            (m) => m.FinanceConfigurationComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'team-configuration',
        loadComponent: () =>
          import('./pages/team-configuration/team-configuration.component').then(
            (m) => m.TeamConfigurationComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'agenda',
        loadComponent: () =>
          import('./pages/agenda/agenda.component').then((m) => m.AgendaComponent),
        canActivate: [canActivate],
      },
      {
        path: 'agenda-configuration',
        loadComponent: () =>
          import('./pages/agenda-configuration/agenda-configuration.component').then(
            (m) => m.AgendaConfigurationComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'appointments',
        loadComponent: () =>
          import('./pages/appointments/appointments.component').then(
            (m) => m.AppointmentsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'appointment-list',
        loadComponent: () =>
          import('./pages/appointment-list/appointment-list.component').then(
            (m) => m.AppointmentListComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'appointment-types',
        loadComponent: () =>
          import('./pages/appointment-types/appointment-types.component').then(
            (m) => m.AppointmentTypesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'appointment-configuration',
        loadComponent: () =>
          import('./pages/appointment-configuration/appointment-configuration.component').then(
            (m) => m.AppointmentConfigurationComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'messages',
        loadComponent: () =>
          import('./pages/messages/messages.component').then(
            (m) => m.MessagesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'messages/:id',
        loadComponent: () =>
          import(
            './pages/message-batch-detail/message-batch-detail.component'
          ).then((m) => m.MessageBatchDetailComponent),
        canActivate: [canActivate],
      },
      {
        path: 'communication-configuration',
        loadComponent: () =>
          import(
            './pages/communication-configuration/communication-configuration.component'
          ).then((m) => m.CommunicationConfigurationComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'loyalty-configuration',
        loadComponent: () =>
          import(
            './pages/loyalty-configuration/loyalty-configuration.component'
          ).then((m) => m.LoyaltyConfigurationComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'activity',
        loadComponent: () =>
          import('./pages/activity/activity.component').then(
            (m) => m.ActivityComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'crm/statuses',
        loadComponent: () =>
          import(
            './pages/crm-lead-statuses/crm-lead-statuses.component'
          ).then((m) => m.CrmLeadStatusesComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'catalog-configuration',
        loadComponent: () =>
          import(
            './pages/catalog-configuration/catalog-configuration.component'
          ).then((m) => m.CatalogConfigurationComponent),
        canActivate: [canActivate],
      },
      {
        path: 'welcome',
        loadComponent: () =>
          import('./pages/welcome/welcome.component').then(
            (m) => m.WelcomeComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'printers',
        loadComponent: () =>
          import('./pages/printers/printers.component').then(
            (m) => m.PrintersComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile/profile.component').then(
            (m) => m.ProfileComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'point-of-sale',
        loadComponent: () =>
          import('./pages/point-of-sale/point-of-sale.component').then(
            (m) => m.PointOfSaleComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'pos-orders',
        redirectTo: 'sales-orders',
        pathMatch: 'full',
      },
      {
        path: 'sales-dashboard',
        loadComponent: () =>
          import('./pages/sales/sales-dashboard/sales-dashboard.component').then(
            (m) => m.SalesDashboardComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'sales-orders/:preset',
        loadComponent: () =>
          import('./pages/sales/sales-orders/sales-orders.component').then(
            (m) => m.SalesOrdersComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'sales-orders',
        loadComponent: () =>
          import('./pages/sales/sales-orders/sales-orders.component').then(
            (m) => m.SalesOrdersComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'sales-order/:id',
        loadComponent: () =>
          import(
            './pages/sales/sales-order-detail/sales-order-detail.component'
          ).then((m) => m.SalesOrderDetailComponent),
        canActivate: [canActivate],
      },
      {
        path: 'unpaid-invoices',
        loadComponent: () =>
          import('./pages/sales/unpaid-invoices/unpaid-invoices.component').then(
            (m) => m.UnpaidInvoicesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'accounting-summary',
        loadComponent: () =>
          import(
            './pages/accounting-summary/accounting-summary.component'
          ).then((m) => m.AccountingSummaryComponent),
        canActivate: [canActivate],
      },
      {
        path: 'sales-overview',
        loadComponent: () =>
          import('./pages/sales/sales-overview/sales-overview.component').then(
            (m) => m.SalesOverviewComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'sales-trends',
        loadComponent: () =>
          import('./pages/sales/sales-trends/sales-trends.component').then(
            (m) => m.SalesTrendsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'product-sales',
        loadComponent: () =>
          import('./pages/product-sales/product-sales.component').then(
            (m) => m.ProductSalesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'customer-sales',
        loadComponent: () =>
          import('./pages/customer-sales/customer-sales.component').then(
            (m) => m.CustomerSalesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'unsold-products',
        loadComponent: () =>
          import('./pages/unsold-products/unsold-products.component').then(
            (m) => m.UnsoldProductsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'seller-sales',
        loadComponent: () =>
          import('./pages/seller-sales/seller-sales.component').then(
            (m) => m.SellerSalesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'session-sales',
        loadComponent: () =>
          import('./pages/session-sales/session-sales.component').then(
            (m) => m.SessionSalesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'country-sales',
        loadComponent: () =>
          import('./pages/country-sales/country-sales.component').then(
            (m) => m.CountrySalesComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'cash-sessions',
        loadComponent: () =>
          import('./pages/cash-sessions/cash-sessions.component').then(
            (m) => m.CashSessionsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'pos-configuration',
        loadComponent: () =>
          import('./pages/pos-configuration/pos-configuration.component').then(
            (m) => m.PosConfigurationComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'customer-configuration',
        loadComponent: () =>
          import(
            './pages/customer-configuration/customer-configuration.component'
          ).then((m) => m.CustomerConfigurationComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'sale-configuration',
        loadComponent: () =>
          import(
            './pages/sales/sale-configuration/sale-configuration.component'
          ).then((m) => m.SaleConfigurationComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'pm-configuration',
        loadComponent: () =>
          import('./pages/pm-configuration/pm-configuration.component').then(
            (m) => m.PmConfigurationComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'inventory',
        loadComponent: () =>
          import('./pages/inventory/inventory.component').then(
            (m) => m.InventoryComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'stock-count/:id',
        loadComponent: () =>
          import('./pages/stock-count/stock-count.component').then(
            (m) => m.StockCountComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'stock-counts',
        loadComponent: () =>
          import('./pages/stock-counts/stock-counts.component').then(
            (m) => m.StockCountsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'product-scan',
        loadComponent: () =>
          import('./pages/product-scan/product-scan.component').then(
            (m) => m.ProductScanComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'product-labels',
        loadComponent: () =>
          import('./pages/product-labels/product-labels.component').then(
            (m) => m.ProductLabelsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'picking',
        loadComponent: () =>
          import('./pages/picking/picking.component').then(
            (m) => m.PickingComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'preparation-configuration',
        loadComponent: () =>
          import(
            './pages/preparation-configuration/preparation-configuration.component'
          ).then((m) => m.PreparationConfigurationComponent),
        canActivate: [canActivate],
      },
      {
        path: 'shipments',
        loadComponent: () =>
          import('./pages/shipments/shipments.component').then(
            (m) => m.ShipmentsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'shipment-configuration',
        loadComponent: () =>
          import('./pages/shipment-configuration/shipment-configuration.component').then(
            (m) => m.ShipmentConfigurationComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'shipping-methods',
        loadComponent: () =>
          import('./pages/shipping-methods/shipping-methods.component').then(
            (m) => m.ShippingMethodsComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'packaging-templates',
        loadComponent: () =>
          import(
            './pages/packaging-templates/packaging-templates.component'
          ).then((m) => m.PackagingTemplatesComponent),
        canActivate: [canActivate],
      },
      {
        path: 'packaging/:id',
        loadComponent: () =>
          import('./pages/packaging/packaging.component').then(
            (m) => m.PackagingComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'packaging',
        loadComponent: () =>
          import('./pages/packaging/packaging.component').then(
            (m) => m.PackagingComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'preparation-orders',
        loadComponent: () =>
          import(
            './pages/preparation-orders/preparation-orders.component'
          ).then((m) => m.PreparationOrdersComponent),
        canActivate: [canActivate],
      },
      {
        path: 'my-preparation-stats',
        loadComponent: () =>
          import(
            './pages/my-preparation-stats/my-preparation-stats.component'
          ).then((m) => m.MyPreparationStatsComponent),
        canActivate: [canActivate],
      },
      {
        path: 'preparation-stats-dashboard',
        loadComponent: () =>
          import(
            './pages/preparation-stats-dashboard/preparation-stats-dashboard.component'
          ).then((m) => m.PreparationStatsDashboardComponent),
        canActivate: [canActivate],
      },
      {
        path: 'purchase',
        loadComponent: () =>
          import('./pages/purchase/purchase.component').then(
            (m) => m.PurchaseComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'purchase-configuration',
        loadComponent: () =>
          import(
            './pages/purchase-configuration/purchase-configuration.component'
          ).then((m) => m.PurchaseConfigurationComponent),
        canActivate: [canActivate],
      },
      {
        path: 'purchase-orders',
        loadComponent: () =>
          import('./pages/purchase-orders/purchase-orders.component').then(
            (m) => m.PurchaseOrdersComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'purchase-reception',
        loadComponent: () =>
          import(
            './pages/purchase-reception/purchase-reception.component'
          ).then((m) => m.PurchaseReceptionComponent),
        canActivate: [canActivate],
      },
      {
        path: 'quick-reception',
        loadComponent: () =>
          import('./pages/quick-reception/quick-reception.component').then(
            (m) => m.QuickReceptionComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./pages/users/users.component').then(
            (m) => m.UsersComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'email-configuration',
        loadComponent: () =>
          import(
            './pages/email-configuration/email-configuration.component'
          ).then((m) => m.EmailConfigurationComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'einvoicing-configuration',
        loadComponent: () =>
          import(
            './pages/einvoicing-configuration/einvoicing-configuration.component'
          ).then((m) => m.EinvoicingConfigurationComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'einvoicing',
        loadComponent: () =>
          import('./pages/einvoicing/einvoicing.component').then(
            (m) => m.EinvoicingComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'compliance-product-types',
        loadComponent: () =>
          import(
            './pages/compliance-product-types/compliance-product-types.component'
          ).then((m) => m.ComplianceProductTypesComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'compliance-products/:productTypeId',
        loadComponent: () =>
          import(
            './pages/compliance-products/compliance-products.component'
          ).then((m) => m.ComplianceProductsComponent),
        canActivate: [canActivate],
      },
      {
        path: 'armory-dashboard',
        loadComponent: () =>
          import('./pages/armory-dashboard/armory-dashboard.component').then(
            (m) => m.ArmoryDashboardComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'registry/new',
        loadComponent: () =>
          import('./pages/registry-entry/registry-entry.component').then(
            (m) => m.RegistryEntryComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'registry/type/:typeId/new',
        loadComponent: () =>
          import('./pages/registry-entry/registry-entry.component').then(
            (m) => m.RegistryEntryComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'registry/type/:typeId',
        loadComponent: () =>
          import('./pages/registry/registry.component').then(
            (m) => m.RegistryComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'registry-types',
        loadComponent: () =>
          import('./pages/registry-types/registry-types.component').then(
            (m) => m.RegistryTypesComponent
          ),
        canActivate: [canAdminActivate],
      },
      {
        path: 'compliance-profiles',
        loadComponent: () =>
          import(
            './pages/compliance-profiles/compliance-profiles.component'
          ).then((m) => m.ComplianceProfilesComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'compliance-exception-reasons',
        loadComponent: () =>
          import(
            './pages/compliance-exception-reasons/compliance-exception-reasons.component'
          ).then((m) => m.ComplianceExceptionReasonsComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'compliance-document-templates',
        loadComponent: () =>
          import(
            './pages/compliance-document-templates/compliance-document-templates.component'
          ).then((m) => m.ComplianceDocumentTemplatesComponent),
        canActivate: [canAdminActivate],
      },
      {
        path: 'armory/compliance-product-types',
        loadComponent: () =>
          import(
            './pages/compliance-product-types/compliance-product-types.component'
          ).then((m) => m.ComplianceProductTypesComponent),
        data: { module: 'armory' },
        canActivate: [canAdminActivate],
      },
      {
        path: 'armory/compliance-shop-attributes',
        loadComponent: () =>
          import(
            './pages/compliance-shop-attributes/compliance-shop-attributes.component'
          ).then((m) => m.ComplianceShopAttributesComponent),
        data: { module: 'armory' },
        canActivate: [canAdminActivate],
      },
      {
        path: 'armory/customer-document-types',
        loadComponent: () =>
          import(
            './pages/customer-document-types/customer-document-types.component'
          ).then((m) => m.CustomerDocumentTypesComponent),
        data: { module: 'armory' },
        canActivate: [canAdminActivate],
      },
      {
        path: 'armory/registry-types',
        loadComponent: () =>
          import('./pages/registry-types/registry-types.component').then(
            (m) => m.RegistryTypesComponent
          ),
        data: { module: 'armory' },
        canActivate: [canAdminActivate],
      },
      {
        path: 'armory/compliance-profiles',
        loadComponent: () =>
          import(
            './pages/compliance-profiles/compliance-profiles.component'
          ).then((m) => m.ComplianceProfilesComponent),
        data: { module: 'armory' },
        canActivate: [canAdminActivate],
      },
      {
        path: 'armory/compliance-exception-reasons',
        loadComponent: () =>
          import(
            './pages/compliance-exception-reasons/compliance-exception-reasons.component'
          ).then((m) => m.ComplianceExceptionReasonsComponent),
        data: { module: 'armory' },
        canActivate: [canAdminActivate],
      },
      {
        path: 'armory/compliance-document-templates',
        loadComponent: () =>
          import(
            './pages/compliance-document-templates/compliance-document-templates.component'
          ).then((m) => m.ComplianceDocumentTemplatesComponent),
        data: { module: 'armory' },
        canActivate: [canAdminActivate],
      },
      {
        path: 'workshop',
        loadComponent: () =>
          import('./pages/workshop/workshop.component').then(
            (m) => m.WorkshopComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'workshop-configuration',
        loadComponent: () =>
          import(
            './pages/workshop-configuration/workshop-configuration.component'
          ).then((m) => m.WorkshopConfigurationComponent),
        canActivate: [canActivate],
      },
      {
        path: 'consignment',
        loadComponent: () =>
          import('./pages/consignment/consignment.component').then(
            (m) => m.ConsignmentComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'consignment-deposit/:id',
        loadComponent: () =>
          import('./pages/consignment-deposit/consignment-deposit.component').then(
            (m) => m.ConsignmentDepositComponent
          ),
        canActivate: [canActivate],
      },
      {
        path: 'consignment-configuration',
        loadComponent: () =>
          import(
            './pages/consignment-configuration/consignment-configuration.component'
          ).then((m) => m.ConsignmentConfigurationComponent),
        canActivate: [canActivate],
      },
      {
        path: 'access-denied',
        loadComponent: () =>
          import('./pages/access-denied/access-denied.component').then(
            (m) => m.AccessDeniedComponent
          ),
      },
      {
        path: 'maintenance',
        loadComponent: () =>
          import('./pages/maintenance/maintenance.component').then(
            (m) => m.MaintenanceComponent
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'start',
  },
];
